import { SortingState } from "@tanstack/react-table";

/**
 * Maps TanStack Table column ids to the backend sort keys accepted by the
 * Portal patient list (and its NavigationIds endpoint). Columns absent from
 * this map are not server-sortable. Kept in one place so the list table and
 * the patient-page navigation arrows always sort by the same keys.
 */
export const PATIENT_LIST_SORT_BY_BACKEND_KEY: Record<string, string> = {
  name: "lastName",
  hospitalId: "hospitalId",
  id: "id",
  alerts: "alerts",
  signUpDate: "signUpDate",
  lastUsed: "lastUsed",
  lastReviewedBy: "lastReviewedBy",
  surveyReceived: "surveyReceived",
  surveyResult: "surveyResult",
  // EN-5123: the RAG measurement columns are dynamic — their column id IS the measurement code the
  // backend sorts by, so they are passed through instead of being listed here.
};

/**
 * Derives the backend (sortBy, sortDir) pair from a TanStack `SortingState`.
 * Returns empty values when there is no active or sortable sort, which the
 * backend treats as its default order (by id).
 */
export function getPatientListSortParams(
  sorting: SortingState | undefined,
  /**
   * EN-5123: measurement codes that may appear as RAG column ids. Passed in (rather than inferred)
   * so an unrecognised id can still be dropped — the backend rejects sort keys it doesn't know, and
   * a bad one would blank the list.
   */
  ragMeasurementCodes?: Iterable<string>
): {
  sortBy?: string;
  sortDir?: "asc" | "desc";
} {
  const activeSort = sorting?.[0];
  if (!activeSort) return {};

  // EN-5067: dynamic custom-field columns use a `custom:{definitionId}` id that the backend
  // accepts verbatim as the sort key, so pass it straight through. EN-5123: so do the dynamic RAG
  // columns, whose id is the measurement code.
  let sortBy: string | undefined;
  if (activeSort.id.startsWith("custom:")) {
    sortBy = activeSort.id;
  } else if (PATIENT_LIST_SORT_BY_BACKEND_KEY[activeSort.id]) {
    sortBy = PATIENT_LIST_SORT_BY_BACKEND_KEY[activeSort.id];
  } else if (new Set(ragMeasurementCodes ?? []).has(activeSort.id)) {
    sortBy = activeSort.id;
  }

  if (!sortBy) return {};

  return { sortBy, sortDir: activeSort.desc ? "desc" : "asc" };
}

/**
 * True when `page` lies past the last page of `totalCount` rows (EN-5277).
 * The API answers such a page with no rows and the real total, so the list
 * shows "(7)" over an empty table — and Pagination hides itself for a single
 * page, leaving no way back.
 */
export function isPageOutOfRange(
  page: number,
  totalCount: number,
  pageSize: number
): boolean {
  return page > Math.max(1, Math.ceil(totalCount / pageSize));
}
